import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, ApiError } from "@/lib/api";
import { centsToInput, maskReaisInput, parseReaisInput } from "@/lib/format";
import type { User } from "@/data/mock";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const MAX_NOME = 150;

const cpfMask = (v: string) =>
  v
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

/** O DTO entrega o salário em reais; o input espera "8.500,00". */
const formatReaisInput = (v: number | null | undefined) =>
  v === null || v === undefined ? "" : centsToInput(Math.round(v * 100));

interface Props {
  usuario: User;
  open: boolean;
  onClose: () => void;
  onSuccess: (atualizado: User) => void;
}

export const EditarCadastroModal = ({ usuario, open, onClose, onSuccess }: Props) => {
  const [nome, setNome] = useState(usuario.name);
  const [cpf, setCpf] = useState(cpfMask(usuario.cpf ?? ""));
  const [salario, setSalario] = useState(formatReaisInput(usuario.salarioFixo));
  const [complemento, setComplemento] = useState(formatReaisInput(usuario.complementoSalario));
  const [admissao, setAdmissao] = useState(usuario.dataAdmissao ?? "");
  const [ativo, setAtivo] = useState(usuario.ativo ?? true);
  const [vinculo, setVinculo] = useState<"pj" | "clt">(usuario.vinculo ?? "pj");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNome(usuario.name);
    setCpf(cpfMask(usuario.cpf ?? ""));
    setSalario(formatReaisInput(usuario.salarioFixo));
    setComplemento(formatReaisInput(usuario.complementoSalario));
    setAdmissao(usuario.dataAdmissao ?? "");
    setAtivo(usuario.ativo ?? true);
    setVinculo(usuario.vinculo ?? "pj");
  }, [open, usuario]);

  const handleSalvar = async () => {
    const nomeTrim = nome.trim();
    if (!nomeTrim) {
      toast.error("Nome não pode ser vazio");
      return;
    }

    const cpfDigits = cpf.replace(/\D/g, "");
    if (cpfDigits.length > 0 && cpfDigits.length !== 11) {
      toast.error("CPF deve ter 11 dígitos");
      return;
    }

    let salarioReais: number | null;
    try {
      salarioReais = salario.trim() ? parseReaisInput(salario) : null;
      if (salarioReais !== null && salarioReais < 0) throw new Error("negativo");
    } catch {
      toast.error("Salário fixo inválido. Use apenas números, ex: 8500,00");
      return;
    }

    // Complemento é só do PJ; para CLT nem aparece e não é enviado.
    let complementoReais: number | null = null;
    if (vinculo === "pj") {
      try {
        complementoReais = complemento.trim() ? parseReaisInput(complemento) : null;
        if (complementoReais !== null && complementoReais < 0) throw new Error("negativo");
      } catch {
        toast.error("Complemento de salário inválido. Use apenas números, ex: 2000,00");
        return;
      }
    }

    // Só chama o endpoint do que realmente mudou — cada um grava sua própria
    // entrada de auditoria, então disparar tudo poluiria a trilha.
    const mudouNome = nomeTrim !== usuario.name;
    const mudouCpf = cpfDigits !== (usuario.cpf ?? "");
    const mudouSalario =
      salarioReais !== (usuario.salarioFixo ?? null) ||
      (vinculo === "pj" && complementoReais !== (usuario.complementoSalario ?? null));
    const mudouAdmissao = vinculo === "pj" && (admissao || null) !== (usuario.dataAdmissao ?? null);
    const mudouAtivo = ativo !== (usuario.ativo ?? true);
    const mudouVinculo = vinculo !== (usuario.vinculo ?? "pj");

    if (!mudouNome && !mudouCpf && !mudouSalario && !mudouAdmissao && !mudouAtivo && !mudouVinculo) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      let atualizado: User = usuario;
      if (mudouNome) {
        // Esta rota devolve só { id, name } de propósito — é a única aberta a
        // não-RH, então não trafega CPF nem salário. Mesclamos localmente.
        const { name } = await api.put<Pick<User, "id" | "name">>(
          `/colaboradores/${usuario.id}/nome`,
          { name: nomeTrim },
        );
        atualizado = { ...atualizado, name };
      }
      if (mudouCpf) {
        atualizado = await api.patch<User>(`/colaboradores/${usuario.id}/documentos`, {
          cpf: cpfDigits.length === 11 ? cpfDigits : null,
        });
      }
      if (mudouVinculo) {
        // Antes do salário: o complemento só é aceito para PJ.
        atualizado = await api.patch<User>(`/colaboradores/${usuario.id}/vinculo`, { vinculo });
      }
      if (mudouSalario) {
        atualizado = await api.patch<User>(`/colaboradores/${usuario.id}/remuneracao`, {
          salarioFixo: salarioReais,
          ...(vinculo === "pj" && { complementoSalario: complementoReais }),
        });
      }
      if (mudouAdmissao) {
        atualizado = await api.patch<User>(`/colaboradores/${usuario.id}/admissao`, {
          dataAdmissao: admissao || null,
        });
      }
      if (mudouAtivo) {
        atualizado = await api.patch<User>(`/colaboradores/${usuario.id}/ativo`, { ativo });
      }

      toast.success("Cadastro atualizado");
      onSuccess(atualizado);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        toast.error("Você não tem permissão para editar este usuário");
      } else {
        toast.error(err instanceof Error ? err.message : "Erro ao atualizar cadastro");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar cadastro</DialogTitle>
          <DialogDescription>
            CPF e salário fixo são usados no cálculo da folha de pagamento. Admissão, dependentes,
            férias e afastamentos do CLT ficam em Dados CLT (ícone de maleta).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cad-nome">Nome completo</Label>
            <Input
              id="cad-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={MAX_NOME}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cad-cpf">CPF</Label>
            <Input
              id="cad-cpf"
              value={cpf}
              onChange={(e) => setCpf(cpfMask(e.target.value))}
              placeholder="000.000.000-00"
              inputMode="numeric"
            />
            <p className="text-xs text-muted-foreground">
              Usado para casar o colaborador nas planilhas de comissão e Amil.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Vínculo</Label>
            <RadioGroup
              value={vinculo}
              onValueChange={(v) => setVinculo(v as "pj" | "clt")}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="pj" id="vinculo-pj" />
                <Label htmlFor="vinculo-pj" className="font-normal">
                  PJ
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="clt" id="vinculo-clt" />
                <Label htmlFor="vinculo-clt" className="font-normal">
                  CLT
                </Label>
              </div>
            </RadioGroup>
            <p className="text-xs text-muted-foreground">
              {vinculo === "clt"
                ? "Não emite NF. O holerite é calculado pelo sistema (INSS, IR, FGTS, férias e 13º)."
                : "Emite NF; salário, comissões, Amil e empréstimos são calculados pela folha PJ."}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cad-salario">Salário fixo (R$)</Label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                R$
              </span>
              <Input
                id="cad-salario"
                value={salario}
                onChange={(e) => setSalario(maskReaisInput(e.target.value))}
                placeholder="0,00"
                inputMode="numeric"
                className="pl-9 tabular-nums"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Deixe vazio se o colaborador recebe apenas comissão.
              {vinculo === "pj" && " É o valor da NF de salário e a base da bonificação."}
            </p>
          </div>

          {vinculo === "pj" && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="cad-complemento">Complemento de salário (R$)</Label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    R$
                  </span>
                  <Input
                    id="cad-complemento"
                    value={complemento}
                    onChange={(e) => setComplemento(maskReaisInput(e.target.value))}
                    placeholder="0,00"
                    inputMode="numeric"
                    className="pl-9 tabular-nums"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Soma no salário, mas não entra na NF nem na bonificação: sai nos benefícios (Ajuda de Custo
                  Remuneração).
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cad-admissao">Data de admissão</Label>
                <Input id="cad-admissao" type="date" value={admissao} onChange={(e) => setAdmissao(e.target.value)} />
                <p className="text-xs text-muted-foreground">Base da experiência e do proporcional da bonificação.</p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="cad-ativo">Colaborador ativo</Label>
              <p className="text-xs text-muted-foreground">
                Inativos não entram em novas folhas de pagamento.
              </p>
            </div>
            <Switch id="cad-ativo" checked={ativo} onCheckedChange={setAtivo} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSalvar} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
